import { createSelector } from 'reselect';

import { AppState } from '../../../../../../store/models/app-state';
import { OverviewFormValues } from '../../models/overview-form-values';
import { userSelector } from '../../../../../../common/selectors/user-selectors';

const demandsState = (state: AppState) => state.demandDetail;

/**
 * The defaults the step fills in for values the server left empty.
 *
 * Shared with the unsaved-changes baseline (#2159) — apart from the start date,
 * which falls back to "in two hours". That is a different moment on every call
 * and would read as a change for the rest of the session; a demand the server
 * has always carries one, and a new one has no baseline to compare against.
 */
const withDefaults = (
    values: OverviewFormValues,
    user: ReturnType<typeof userSelector>,
    startFallback?: Date
): OverviewFormValues => ({
    ...values,
    title: values.title ?? {},
    description: values.description ?? {},
    internalNote: values.internalNote ?? '',
    noteToCustomer: values.noteToCustomer ?? {},
    startDate: values.startDate ?? startFallback,
    endDate: values.endDate,
    isScheduled: values.isScheduled ?? false,
    responsibleContact: values.responsibleContact ?? user.contact,
});

export const demandDetailOverviewDataSelector = createSelector(demandsState, userSelector, (state, user) => {
    const now = new Date();

    return {
        ...state.overview.stepData,
        values: withDefaults(state.overview.stepData.values, user, new Date(now.setHours(now.getHours() + 2))),
    };
});

/** What the step compares against to decide whether anything is unsaved. */
export const demandDetailOverviewBaselineSelector = createSelector(demandsState, userSelector, (state, user) =>
    state.overview.baseline ? withDefaults(state.overview.baseline, user) : undefined
);

export const demandDetailStartDateSelector = createSelector(
    demandsState,
    userSelector,
    state => state.overview.stepData.values.startDate ?? new Date()
);

export const demandDetailIsScheduledSelector = createSelector(
    demandsState,
    userSelector,
    state => state.overview.stepData.values.isScheduled ?? false
);
